import axios from 'axios'
import { getCookie } from 'cookies-next'
import { AUTH_JWT_COOKIE } from '@/constants/cookies'

// Recurly base URL for constructing management links
export const RECURLY_BASE_URL = 'https://angel.recurly.com'

export interface CreateRecurlyAccountSubscriptionInput {
  email: string
  token: string
  planCode: string
  userId: string
  /** Optional coupon code (e.g., from a sale promotion) to apply to the subscription */
  couponCode?: string
}

/**
 * Creates a Recurly account subscription via the BFF.
 */
export async function createRecurlyAccountSubscription(input: CreateRecurlyAccountSubscriptionInput): Promise<void> {
  const authToken = getCookie(AUTH_JWT_COOKIE)

  await axios.post(
    `${process.env.NEXT_PUBLIC_BFF_API_URL}/recurly/create-account-subscription`,
    {
      email: input.email,
      token: input.token,
      planCode: input.planCode,
      userId: input.userId,
      // Forward optional coupon to the BFF if provided
      couponCode: input.couponCode,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        ...(typeof authToken === 'string' ? { Authorization: authToken } : {}),
      },
    },
  )
}

/**
 * Retrieves a Recurly hosted login token for the current authenticated user via the BFF.
 * The token can be appended to https://angel.recurly.com/account/ to generate an authenticated management URL.
 */
export async function getRecurlyHostedLoginToken(accountId: string): Promise<string> {
  const authToken = getCookie(AUTH_JWT_COOKIE)

  const response = await axios.get<{ hosted_login_token: string }>(
    `${process.env.NEXT_PUBLIC_BFF_API_URL}/recurly/hosted-login-token`,
    {
      params: { accountId },
      headers: {
        'Content-Type': 'application/json',
        ...(typeof authToken === 'string' ? { Authorization: authToken } : {}),
      },
    },
  )

  return response.data.hosted_login_token
}

/**
 * Returns a fully-qualified, authenticated Recurly account management URL for the given userId.
 * Builds the accountId as `code-${userId}`, fetches a hosted login token, and appends it to the account URL path.
 */
export async function getRecurlyManagementUrlForUser(userId: string): Promise<string> {
  const accountId = `code-${userId}`
  const token = await getRecurlyHostedLoginToken(accountId)
  return `${RECURLY_BASE_URL}/account/${token}`
}

export default createRecurlyAccountSubscription
